import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { stripHtmlFromLabel } from "../utils/textFormatting";

interface DecisionData {
  id?: string;
  name: string;
  description: string;
  category: string;
  status: string;
  decisionMaker: string;
  stakeholders: string;
  criteria: string;
  alternatives: string;
  selectedOption: string;
  rationale: string;
  implications: string;
  decisionDate?: Date | string;
  reviewDate?: Date | string;
}

interface DecisionDialogProps {
  visible: boolean;
  decisionData?: DecisionData;
  setShowDecisionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleSaveDecision: (decisionData: DecisionData) => void;
  handleDeleteDecision?: () => void;
}

const DecisionDialog: React.FC<DecisionDialogProps> = ({
  visible,
  decisionData,
  setShowDecisionDialog,
  handleSaveDecision,
  handleDeleteDecision,
}) => {
  const [name, setName] = useState<string>(decisionData?.name || "");
  const [description, setDescription] = useState<string>(
    decisionData?.description || ""
  );
  const [category, setCategory] = useState<string>(
    decisionData?.category || ""
  );
  const [status, setStatus] = useState<string>(
    decisionData?.status || "Pending"
  );
  const [decisionMaker, setDecisionMaker] = useState<string>(
    decisionData?.decisionMaker || ""
  );
  const [stakeholders, setStakeholders] = useState<string>(
    decisionData?.stakeholders || ""
  );
  const [criteria, setCriteria] = useState<string>(
    decisionData?.criteria || ""
  );
  const [alternatives, setAlternatives] = useState<string>(
    decisionData?.alternatives || ""
  );
  const [selectedOption, setSelectedOption] = useState<string>(
    decisionData?.selectedOption || ""
  );
  const [rationale, setRationale] = useState<string>(
    decisionData?.rationale || ""
  );
  const [implications, setImplications] = useState<string>(
    decisionData?.implications || ""
  );
  const [decisionDate, setDecisionDate] = useState<Date | null>(
    decisionData?.decisionDate
      ? typeof decisionData.decisionDate === "string"
        ? new Date(decisionData.decisionDate)
        : decisionData.decisionDate
      : null
  );
  const [reviewDate, setReviewDate] = useState<Date | null>(
    decisionData?.reviewDate
      ? typeof decisionData.reviewDate === "string"
        ? new Date(decisionData.reviewDate)
        : decisionData.reviewDate
      : null
  );

  useEffect(() => {
    if (decisionData) {
      setName(decisionData.name || "");
      setDescription(decisionData.description || "");
      setCategory(decisionData.category || "");
      setStatus(decisionData.status || "Pending");
      setDecisionMaker(decisionData.decisionMaker || "");
      setStakeholders(decisionData.stakeholders || "");
      setCriteria(decisionData.criteria || "");
      setAlternatives(decisionData.alternatives || "");
      setSelectedOption(decisionData.selectedOption || "");
      setRationale(decisionData.rationale || "");
      setImplications(decisionData.implications || "");
      setDecisionDate(
        decisionData.decisionDate
          ? typeof decisionData.decisionDate === "string"
            ? new Date(decisionData.decisionDate)
            : decisionData.decisionDate
          : null
      );
      setReviewDate(
        decisionData.reviewDate
          ? typeof decisionData.reviewDate === "string"
            ? new Date(decisionData.reviewDate)
            : decisionData.reviewDate
          : null
      );
    }
  }, [decisionData]);

  const handleSave = () => {
    if (!name || name.trim() === "") {
      alert("Decision name is required. Please enter a decision name.");
      return;
    }

    handleSaveDecision({
      id: decisionData?.id,
      name: name.trim(),
      description,
      category,
      status,
      decisionMaker,
      stakeholders,
      criteria,
      alternatives,
      selectedOption,
      rationale,
      implications,
      decisionDate: decisionDate || undefined,
      reviewDate: reviewDate || undefined,
    });

    setShowDecisionDialog(false);
  };

  const handleCancel = () => {
    setShowDecisionDialog(false);
  };

  const handleDelete = () => {
    if (handleDeleteDecision) {
      if (
        window.confirm(
          "Are you sure you want to remove this decision from the node?"
        )
      ) {
        handleDeleteDecision();
      }
    }
  };

  const categoryOptions = [
    { label: "Strategic", value: "Strategic" },
    { label: "Tactical", value: "Tactical" },
    { label: "Operational", value: "Operational" },
    { label: "Technical", value: "Technical" },
    { label: "Financial", value: "Financial" },
    { label: "HR", value: "HR" },
    { label: "Legal", value: "Legal" },
    { label: "Other", value: "Other" },
  ];

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Approved", value: "Approved" },
    { label: "Rejected", value: "Rejected" },
    { label: "Deferred", value: "Deferred" },
  ];

  const footer = (
    <div className="decision-dialog-footer">
      {handleDeleteDecision && (
        <Button
          label="Delete"
          icon="pi pi-trash"
          onClick={handleDelete}
          className="p-button-danger"
          style={{ marginRight: "auto" }}
        />
      )}
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
      />
      <Button label="Save" icon="pi pi-check" onClick={handleSave} />
    </div>
  );

  return (
    <Dialog
      header="Decision"
      visible={visible}
      onHide={handleCancel}
      footer={footer}
      className="decision-dialog"
      style={{ width: "600px" }}
      modal
    >
      <div className="decision-dialog-content">
        {/* Name (Required) */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-name">
            Name (edit in map) <span style={{ color: "red" }}>*</span>
          </label>
          <InputText
            id="decision-name"
            value={stripHtmlFromLabel(name)}
            disabled
            style={{ backgroundColor: "#f5f5f5" }}
            placeholder="Enter decision name in map"
          />
        </div>

        {/* Description */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-description">Description</label>
          <InputTextarea
            id="decision-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the decision"
            rows={6}
          />
        </div>

        {/* Category and Status (side by side) */}
        <div className="decision-dialog-row">
          <div className="decision-dialog-field">
            <label htmlFor="decision-category">Category</label>
            <Dropdown
              id="decision-category"
              value={category}
              options={categoryOptions}
              onChange={(e) => setCategory(e.value)}
              placeholder="Select category"
            />
          </div>

          <div className="decision-dialog-field">
            <label htmlFor="decision-status">Status</label>
            <Dropdown
              id="decision-status"
              value={status}
              options={statusOptions}
              onChange={(e) => setStatus(e.value)}
              placeholder="Select status"
            />
          </div>
        </div>

        {/* Decision Maker */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-maker">Decision Maker</label>
          <InputText
            id="decision-maker"
            value={decisionMaker}
            onChange={(e) => setDecisionMaker(e.target.value)}
            placeholder="Who makes the final decision"
          />
        </div>

        {/* Stakeholders */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-stakeholders">Stakeholders</label>
          <InputTextarea
            id="decision-stakeholders"
            value={stakeholders}
            onChange={(e) => setStakeholders(e.target.value)}
            placeholder="List stakeholders involved or affected"
            rows={4}
          />
        </div>

        {/* Decision Criteria */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-criteria">Decision Criteria</label>
          <InputTextarea
            id="decision-criteria"
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            placeholder="What criteria will be used to make the decision"
            rows={4}
          />
        </div>

        {/* Alternatives */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-alternatives">Alternatives Considered</label>
          <InputTextarea
            id="decision-alternatives"
            value={alternatives}
            onChange={(e) => setAlternatives(e.target.value)}
            placeholder="List alternative options considered"
            rows={6}
          />
        </div>

        {/* Selected Option */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-selected">Selected Option</label>
          <InputTextarea
            id="decision-selected"
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
            placeholder="The option that was selected"
            rows={4}
          />
        </div>

        {/* Rationale */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-rationale">Rationale</label>
          <InputTextarea
            id="decision-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Explain why this decision was made"
            rows={6}
          />
        </div>

        {/* Implications */}
        <div className="decision-dialog-field">
          <label htmlFor="decision-implications">Implications</label>
          <InputTextarea
            id="decision-implications"
            value={implications}
            onChange={(e) => setImplications(e.target.value)}
            placeholder="What are the implications and impacts"
            rows={6}
          />
        </div>

        {/* Decision Date and Review Date (side by side) */}
        <div className="decision-dialog-row">
          <div className="decision-dialog-field">
            <label htmlFor="decision-date">Decision Date</label>
            <Calendar
              id="decision-date"
              value={decisionDate}
              onChange={(e) => setDecisionDate(e.value as Date | null)}
              showIcon
              dateFormat="yy-mm-dd"
            />
          </div>

          <div className="decision-dialog-field">
            <label htmlFor="review-date">Review Date</label>
            <Calendar
              id="review-date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.value as Date | null)}
              showIcon
              dateFormat="yy-mm-dd"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DecisionDialog;
